// Normalização de erros vindos da API (marketingbackgo — mesmo formato do ERPApi).
//
// O backend responde erro em dois formatos (internal/handlers/base_handler.go):
//
//   { success: false, error: "Bad Request", message: "Validation failed",
//     details: { "ship_addr_zip": "Maximum length is 8" } }   ← *ValidationErrors
//   { success: false, error: "Bad Request", message: "<texto legível>" }   ← demais erros
//
// O campo `error` é sempre o nome seco do status HTTP; usá-lo como mensagem
// (padrão antigo `body.error || body.message`) fazia a tela mostrar "Bad Request"
// e descartar `details`, que é justamente onde está a informação útil.

/** Nomes de status HTTP que o backend devolve em `error` — nunca servem de mensagem. */
const HTTP_STATUS_NAMES = new Set([
  'Bad Request',
  'Unauthorized',
  'Forbidden',
  'Not Found',
  'Conflict',
  'Internal Server Error',
]);

/** Mensagens do backend que só repetem o tipo do erro, sem dizer o que houve. */
const OPAQUE_MESSAGES = new Set([
  'validation failed',
  'an internal error occurred',
]);

const FALLBACK_BY_STATUS: Record<number, string> = {
  400: 'Alguns campos estão inválidos ou faltando.',
  401: 'Sua sessão expirou. Entre novamente para continuar.',
  403: 'Você não tem permissão para executar esta ação.',
  404: 'O registro não foi encontrado.',
  409: 'A operação conflita com o estado atual do registro.',
  500: 'Ocorreu um erro interno. Tente novamente ou acione o suporte.',
};

/** Código de erro de negócio que o backend manda em `error` (quando não é o nome do status) ou em `code`. */
export const ACCESS_EXPIRED_CODE = 'access_expired';
export const SIGNUP_DISABLED_CODE = 'signup_disabled';

/** Evento disparado no `window` quando uma chamada recebe 403 `access_expired` (ProfileContext recarrega o perfil). */
export const ACCESS_EXPIRED_EVENT = 'plan:access-expired';

export class ApiError extends Error {
  readonly status: number;
  /** Erros por campo (`details` da resposta), quando o backend os envia. */
  readonly details?: Record<string, string>;
  /** Código de negócio (`access_expired`, `signup_disabled`), quando há. */
  readonly code?: string;

  constructor(message: string, status: number, details?: Record<string, string>, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.code = code;
  }

  /** 403 de período de teste encerrado — a tela "Acesso encerrado" fica na frente; não é erro para mostrar. */
  get isAccessExpired(): boolean {
    return this.status === 403 && this.code === ACCESS_EXPIRED_CODE;
  }
}

/** Extrai o código de negócio do corpo: `code`, ou `error` quando não é um nome de status HTTP. */
const asCode = (body: Record<string, unknown>): string | undefined => {
  const code = body.code;
  if (typeof code === 'string' && code.trim()) return code.trim();
  const err = body.error;
  if (typeof err === 'string' && err.trim() && !HTTP_STATUS_NAMES.has(err.trim()) && /^[a-z0-9_]+$/.test(err.trim())) {
    return err.trim();
  }
  return undefined;
};

const asDetails = (raw: unknown): Record<string, string> | undefined => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [field, msg] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof msg === 'string' && msg.trim()) out[field] = msg;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

const isUsable = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.trim() !== '' &&
  !HTTP_STATUS_NAMES.has(value.trim()) &&
  !OPAQUE_MESSAGES.has(value.trim().toLowerCase());

/**
 * Lê o corpo de uma resposta com falha e devolve um `ApiError` já com a
 * mensagem escolhida e os erros por campo preservados.
 *
 * `fallback` é usado só quando o backend não mandou nada aproveitável e o
 * status não tem texto padrão — normalmente algo como "Erro ao criar pedido".
 */
export const apiErrorFrom = async (res: Response, fallback: string): Promise<ApiError> => {
  const body = await res.json().catch(() => ({} as Record<string, unknown>));
  const details = asDetails((body as Record<string, unknown>).details);
  const code = asCode(body as Record<string, unknown>);

  // Quando há erros por campo, a lista já é a explicação; a mensagem vira só o
  // cabeçalho do modal.
  const message = details
    ? 'Alguns campos precisam ser corrigidos antes de salvar.'
    : (isUsable((body as Record<string, unknown>).message)
        ? String((body as Record<string, unknown>).message)
        : isUsable((body as Record<string, unknown>).error)
          ? String((body as Record<string, unknown>).error)
          : FALLBACK_BY_STATUS[res.status] ?? fallback);

  const error = new ApiError(message, res.status, details, code);

  // Período de teste encerrado: avisa o app (o ProfileContext recarrega o perfil e a tela de
  // "acesso encerrado" assume), em vez de cada tela abrir um modal de erro.
  if (error.isAccessExpired && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(ACCESS_EXPIRED_EVENT));
  }

  return error;
};
